import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Save, Truck, ArrowDownToLine, ArrowUpFromLine, Loader2, Info } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  getEdiPartners, createEdiPartner, deleteEdiPartner,
  getEdiMappings, createEdiMapping, updateEdiMapping, deleteEdiMapping
} from '../../config/api';

// Curated field catalogs (suggestions only — any value is allowed, so this stays flexible).
// Inbound 844: our internal fields that a wholesaler's EDI element maps INTO.
const INBOUND_844_TARGETS = [
  'claimNumber', 'poNumber', 'wholesalerName',
  'customerName', 'customerDea', 'customerHin', 'customer340B', 'customerGln', 'customerNpi', 'customerIdRef',
  'customerAddress', 'customerCity', 'customerState', 'customerZip',
  'productNdc', 'productName', 'quantity', 'unitOfMeasure', 'unitPrice', 'contractPrice', 'chargebackAmount',
  'invoiceNumber', 'invoiceDate', 'saleDate', 'contractNumber'
];
// Outbound 849: our internal fields we emit FROM in the response.
const OUTBOUND_849_SOURCES = [
  'claimNumber', 'lineNumber', 'responseStatus', 'reasonCode',
  'approvedQuantity', 'approvedAmount', 'chargebackAmount', 'discrepancyAmount', 'message'
];
const SUGGESTED_WHOLESALERS = ['McKesson', 'Cardinal Health', 'Cencora'];

const isaFromName = (name) => name.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 15) || 'PARTNER';

// One editable mapping direction (a card with a table of source→target rows).
function DirectionCard({ title, subtitle, icon: Icon, tone, partnerId, transactionSet, sourceLabel, targetLabel, sourceSuggestions, targetSuggestions }) {
  const [rows, setRows] = useState([]);
  const [original, setOriginal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletedIds, setDeletedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEdiMappings({ partnerId, transactionSet });
      const mapped = data.map((m) => ({ id: m.id, sourceField: m.sourceField, targetField: m.targetField, transform: m.transform || '' }));
      setRows(mapped);
      setOriginal(mapped);
      setDeletedIds([]);
    } catch (e) {
      toast.error('Failed to load mappings');
    } finally {
      setLoading(false);
    }
  }, [partnerId, transactionSet]);

  useEffect(() => { load(); }, [load]);

  const addRow = () => setRows((r) => [...r, { id: null, sourceField: '', targetField: '', transform: '' }]);
  const updateRow = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));
  const removeRow = (i) => setRows((r) => {
    const row = r[i];
    if (row.id) setDeletedIds((d) => [...d, row.id]);
    return r.filter((_, idx) => idx !== i);
  });

  const save = async () => {
    const invalid = rows.some((row) => (row.sourceField.trim() && !row.targetField.trim()) || (!row.sourceField.trim() && row.targetField.trim()));
    if (invalid) return toast.error('Each row needs both a source and a target field');
    setSaving(true);
    try {
      // Deletes
      for (const id of deletedIds) await deleteEdiMapping(id);
      // Creates + updates
      for (const row of rows) {
        if (!row.sourceField.trim() || !row.targetField.trim()) continue;
        const payload = { partnerId, transactionSet, sourceField: row.sourceField.trim(), targetField: row.targetField.trim(), transform: row.transform.trim() || null, active: true };
        if (row.id) {
          const orig = original.find((o) => o.id === row.id);
          if (orig && (orig.sourceField !== payload.sourceField || orig.targetField !== payload.targetField || (orig.transform || '') !== (payload.transform || ''))) {
            await updateEdiMapping(row.id, payload);
          }
        } else {
          await createEdiMapping(payload);
        }
      }
      toast.success(`${title} saved`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save mappings');
    } finally {
      setSaving(false);
    }
  };

  const dirty = JSON.stringify(rows) !== JSON.stringify(original) || deletedIds.length > 0;
  const tones = {
    blue: { border: 'border-blue-200', head: 'bg-blue-50 text-blue-700', icon: 'text-blue-600' },
    emerald: { border: 'border-emerald-200', head: 'bg-emerald-50 text-emerald-700', icon: 'text-emerald-600' }
  }[tone];

  return (
    <div className={`rounded-xl border ${tones.border} bg-white shadow-sm`}>
      <div className={`flex items-center justify-between gap-3 rounded-t-xl ${tones.head} px-4 py-3`}>
        <div className="flex items-center gap-2">
          <Icon className={`h-5 w-5 ${tones.icon}`} />
          <div>
            <h3 className="text-sm font-semibold">{title}</h3>
            <p className="text-xs opacity-80">{subtitle}</p>
          </div>
        </div>
        <button onClick={save} disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-primary-700 disabled:opacity-50">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
        </button>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="py-8 text-center text-sm text-gray-400">Loading…</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="px-2 py-1.5 font-medium">{sourceLabel}</th>
                    <th className="px-2 py-1.5 font-medium">{targetLabel}</th>
                    <th className="px-2 py-1.5 font-medium">Transform</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={4} className="px-2 py-6 text-center text-sm text-gray-400">No fields mapped yet. Add a row to start.</td></tr>
                  ) : rows.map((row, i) => (
                    <tr key={i}>
                      <td className="px-1 py-1">
                        <input list={`src-${transactionSet}`} value={row.sourceField} onChange={(e) => updateRow(i, 'sourceField', e.target.value)}
                          placeholder={sourceLabel} className="w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-primary-500 focus:outline-none" />
                      </td>
                      <td className="px-1 py-1">
                        <input list={`tgt-${transactionSet}`} value={row.targetField} onChange={(e) => updateRow(i, 'targetField', e.target.value)}
                          placeholder={targetLabel} className="w-full rounded-md border border-gray-300 px-2 py-1.5 font-mono text-xs focus:border-primary-500 focus:outline-none" />
                      </td>
                      <td className="px-1 py-1">
                        <input value={row.transform} onChange={(e) => updateRow(i, 'transform', e.target.value)}
                          placeholder="e.g. UPPER, DATE:YYYYMMDD" className="w-full rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-500 focus:border-primary-500 focus:outline-none" />
                      </td>
                      <td className="px-1 py-1 text-right">
                        <button onClick={() => removeRow(i)} title="Remove" className="text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <datalist id={`src-${transactionSet}`}>{sourceSuggestions.map((s) => <option key={s} value={s} />)}</datalist>
            <datalist id={`tgt-${transactionSet}`}>{targetSuggestions.map((s) => <option key={s} value={s} />)}</datalist>
            <button onClick={addRow} className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-800">
              <Plus className="h-3.5 w-3.5" /> Add field
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function WholesalerEdiMapping() {
  const [partners, setPartners] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [newName, setNewName] = useState('');

  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getEdiPartners();
      setPartners(list);
      setSelectedId((cur) => cur || (list[0]?.id ?? null));
    } catch (e) {
      toast.error('Failed to load wholesalers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  const addWholesaler = async (name) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setAdding(true);
    try {
      const created = await createEdiPartner({
        name: trimmed, partnerType: 'WHOLESALER', isaId: isaFromName(trimmed),
        isaQualifier: 'ZZ', transactionSets: '844,849', usageIndicator: 'P', ackRequested: true, active: true
      });
      toast.success(`${trimmed} added`);
      setNewName('');
      await loadPartners();
      if (created?.id) setSelectedId(created.id);
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.errors?.[0]?.msg || 'Failed to add wholesaler');
    } finally {
      setAdding(false);
    }
  };

  const removeWholesaler = async (p) => {
    if (!window.confirm(`Delete wholesaler "${p.name}"? This also removes its inbound 844 and outbound 849 field maps. This cannot be undone.`)) return;
    setDeletingId(p.id);
    try {
      await deleteEdiPartner(p.id);
      toast.success(`${p.name} deleted`);
      if (selectedId === p.id) setSelectedId(null);
      await loadPartners();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to delete wholesaler');
    } finally {
      setDeletingId(null);
    }
  };

  const selected = partners.find((p) => p.id === selectedId);
  const existingNames = new Set(partners.map((p) => p.name.toLowerCase()));

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <Truck className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Wholesaler EDI Mapping</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500">Define flexible, per-wholesaler field maps — a separate <strong>inbound 844</strong> map and <strong>outbound 849</strong> map for each trading partner. No code required.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Wholesaler list */}
        <div className="lg:col-span-1">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 px-4 py-3">
              <h2 className="text-sm font-semibold text-gray-900">Wholesalers</h2>
            </div>
            <div className="p-2">
              {loading ? (
                <p className="px-2 py-4 text-sm text-gray-400">Loading…</p>
              ) : partners.length === 0 ? (
                <p className="px-2 py-3 text-xs text-gray-400">No wholesalers yet. Add one below.</p>
              ) : partners.map((p) => (
                <div key={p.id}
                  className={`group mb-1 flex items-center gap-1 rounded-lg pr-1 transition-colors ${selectedId === p.id ? 'bg-primary-50 ring-1 ring-primary-200' : 'hover:bg-gray-50'}`}>
                  <button onClick={() => setSelectedId(p.id)}
                    className={`min-w-0 flex-1 rounded-lg px-3 py-2 text-left text-sm ${selectedId === p.id ? 'font-semibold text-primary-800' : 'text-gray-700'}`}>
                    <div className="flex items-center justify-between">
                      <span className="truncate">{p.name}</span>
                      {!p.active && <span className="ml-2 text-[10px] uppercase text-gray-400">inactive</span>}
                    </div>
                    <div className="text-[11px] text-gray-400">{p.transactionSets}</div>
                  </button>
                  <button onClick={() => removeWholesaler(p)} disabled={deletingId === p.id}
                    title={`Delete ${p.name}`}
                    className="flex-shrink-0 rounded-md p-1.5 text-gray-300 opacity-60 hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 disabled:opacity-40">
                    {deletingId === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </button>
                </div>
              ))}
            </div>

            {/* Quick add */}
            <div className="border-t border-gray-100 p-3">
              <div className="mb-2 flex flex-wrap gap-1.5">
                {SUGGESTED_WHOLESALERS.filter((n) => !existingNames.has(n.toLowerCase())).map((n) => (
                  <button key={n} onClick={() => addWholesaler(n)} disabled={adding}
                    className="rounded-full border border-dashed border-primary-300 px-2.5 py-1 text-xs text-primary-700 hover:bg-primary-50 disabled:opacity-50">
                    + {n}
                  </button>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); addWholesaler(newName); }} className="flex gap-1.5">
                <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Add wholesaler…"
                  className="w-full rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-primary-500 focus:outline-none" />
                <button type="submit" disabled={adding || !newName.trim()} title="Add"
                  className="rounded-md bg-primary-600 px-2.5 text-white hover:bg-primary-700 disabled:opacity-50"><Plus className="h-4 w-4" /></button>
              </form>
            </div>
          </div>

          <div className="mt-3 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-xs text-blue-700">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>Envelope settings (ISA/GS identifiers, usage) live under <strong>Processing → EDI → EDI Configurations</strong>. New wholesalers get a sensible default you can refine there.</span>
          </div>
        </div>

        {/* Maps */}
        <div className="lg:col-span-3">
          {!selected ? (
            <div className="rounded-xl border border-dashed border-gray-200 bg-white p-12 text-center text-gray-400">
              Select or add a wholesaler to edit its EDI maps.
            </div>
          ) : (
            <div className="space-y-6">
              <div className="rounded-lg bg-gradient-to-r from-primary-700 to-indigo-700 px-5 py-3 text-white">
                <h2 className="text-lg font-bold">{selected.name}</h2>
                <p className="text-xs text-white/80">Editing the inbound 844 and outbound 849 field maps for this wholesaler.</p>
              </div>

              <DirectionCard
                key={`844-${selected.id}`}
                title="Inbound — EDI 844 (Chargeback Requests)"
                subtitle="Wholesaler’s EDI element → our internal claim field"
                icon={ArrowDownToLine} tone="blue"
                partnerId={selected.id} transactionSet="844"
                sourceLabel="Wholesaler field / segment" targetLabel="Our field"
                sourceSuggestions={['ISA06', 'N1*BY', 'N1*ST', 'LIN*NDC', 'QTY', 'CTP', 'REF*IV', 'DTM*011']}
                targetSuggestions={INBOUND_844_TARGETS}
              />

              <DirectionCard
                key={`849-${selected.id}`}
                title="Outbound — EDI 849 (Responses)"
                subtitle="Our internal response field → wholesaler’s EDI element"
                icon={ArrowUpFromLine} tone="emerald"
                partnerId={selected.id} transactionSet="849"
                sourceLabel="Our field" targetLabel="Wholesaler field / segment"
                sourceSuggestions={OUTBOUND_849_SOURCES}
                targetSuggestions={['ACK01', 'ACK*A', 'ACK*R', 'REF*IV', 'QTY', 'AMT', 'MSG']}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
